import React, { useEffect, useRef, useState } from 'react';
import {
	ContentSidebarLayout,
	DragHelper,
	DropdownMenu,
	ItemRowCell,
	useLayout,
	useTranslation,
} from '@ukpostbox/frontend-framework';
import { GetItemCounts } from '@ukpostbox/client-data';
import { ItemRowToolBar } from '@ukpostbox/frontend-framework/lib/Components/ItemRow/ItemRowToolBar';
import { PaginatedItemCountsType } from '@ukpostbox/client-data/lib/Types/GenericTypes';
import {
	DragSelectMouseDown,
	DragSelectMousePos,
	DragSelectSelected,
	DragSelectShiftKeyMouseUp,
} from '../SharedFunctions/DragSelect';
import { ShiftKeyChange, ShiftKeyMouseOver } from '../SharedFunctions/ShiftSelect';
import { useMailbox } from '../../Providers/NewMailboxProvider';
import { MailboxEntries } from './MailboxEntries';
import { MailboxesSelectedMenu } from '../Menus/MailboxesSelectedMenu';
import MailboxNavigation from '../MailboxNavigation';
import { MailboxActions } from '../MailboxActions';
import SelectEverythingBanner from '../../../_Global/Components/SelectEverythingBanner';
import { MailboxSearchHeader } from '../MailboxSearchHeader';
import { MailboxSearchPills } from '../Search/MailboxSearchPills';
import { useMailboxesStore } from '../../Providers/Stores/MailboxesStore';
import { MailboxRootType } from '../Types/MailboxTypes';
import { MailboxHeader } from '../MailboxHeader';
import { MailboxListHeader } from './ListHeaders/MailboxListHeader';
import { DragPositionType, MousePositionType } from '../Types/SharedFunctions/DragAndDropTypes';

function MailboxRoot({
	status,
	error,
	data,
	dataUpdatedAt,
	isFetchingNextPage,
	fetchNextPage,
	hasNextPage,
	isStale,
	isRefetching,
}: MailboxRootType) {
	const { t } = useTranslation();

	const { isTiny, isSmall, isMedium } = useLayout();

	const {
		customFolderData,
		gridSize,
		gridView,
		allSelected,
		visibleSelected,
		visibleIndeterminate,
		selectedItems,
		selectedItemsRef,
		searchActive,
		searchParams,

		MoveSelectedItems,
		HandleFolderMove,
		ToggleAll,
		ToggleVisible,
		ToggleSelected,
		ResetSelection,
	} = useMailbox();

	const mailBoxInfo = useMailboxesStore((state) => state.mailBoxInfo);

	const [itemCounts, setItemCounts] = useState<PaginatedItemCountsType>({ loaded: 0, total: 0 });
	const [draggingActive, setDraggingActive] = useState<boolean>(false);
	const [mouseDown, setMouseDown] = useState<boolean>(false);
	const [dragWasActive, setDragWasActive] = useState<boolean>(false);
	const [disableFolderSelection, setDisableFolderSelection] = useState<boolean>(false);

	const dragPositionRef = useRef<DragPositionType>({ startX: 0, startY: 0, x: 0, y: 0 });
	const dragSelectorRef = useRef<HTMLDivElement>(null);
	const lastMousePosRef = useRef<MousePositionType>({ x: 0, y: 0 });
	const shiftKeyDownRef = useRef<boolean>(false);
	const mouseDownRef = useRef<boolean>(false);
	const mailBoxesRef = useRef<any>(null);
	const mailBoxNavigationRef = useRef<any>(null);
	const gridViewRef = useRef<boolean>(gridView);
	const draggingActiveRef = useRef<boolean>(draggingActive);

	useEffect(() => {
		if (data) {
			setItemCounts(GetItemCounts(data as any));
		}
	}, [dataUpdatedAt, data]);

	useEffect(() => {
		if (dragWasActive && selectedItems.length > 0 && !shiftKeyDownRef.current) {
			ResetSelection();
		}

		document.body.classList.toggle('dragging-active', draggingActive);

		draggingActiveRef.current = draggingActive;
	}, [draggingActive, dragWasActive]);

	useEffect(() => {
		if (dragWasActive) {
			if (!mouseDown) {
				DragSelectSelected({
					draggingWasActiveCallback: (state) => setDragWasActive(state),
					toggleSelectedCallback: (targetKey) => ToggleSelected(targetKey, itemCounts.total, true),
				});
			} else if (selectedItems.length > 0 && !shiftKeyDownRef.current) {
				ResetSelection();
			}
		}
	}, [mouseDown, dragWasActive]);

	useEffect(() => {
		gridViewRef.current = gridView;
	}, [gridView]);

	useEffect(() => {
		document.addEventListener('pointerup', () => {
			DragSelectShiftKeyMouseUp({
				dragSelectorRef,
				mouseDownRef,
				shiftKeyDownRef,
				mouseDownCallback: (state) => {
					if (dragSelectorRef.current) {
						dragSelectorRef.current.style.opacity = '0';
					}

					if (dragSelectorRef.current) {
						dragSelectorRef.current.style.width = '0px';
						dragSelectorRef.current.style.height = '0px';
					}

					mouseDownRef.current = false;
					setMouseDown(state);
				},
				toggleSelectedCallback: (targetKey) => ToggleSelected(targetKey, itemCounts.total, true),
				disableFolderSelectionCallback: (state) => setDisableFolderSelection(state),
			});
		});

		document.addEventListener('pointermove', (e) => {
			lastMousePosRef.current = {
				x: e.x || e.clientX,
				y: e.y || e.clientY,
			};

			DragSelectMousePos({
				event: e,
				enabled: !isTiny && !isSmall && !isMedium,
				dragPositionRef,
				mailBoxesRef,
				mouseDownRef,
				dragSelectorRef,
				disableFolderSelectionCallback: (state) => setDisableFolderSelection(state),
				draggerPositionCallback: (response) => {
					if (dragSelectorRef.current) {
						dragSelectorRef.current.style.left = response.left;
						dragSelectorRef.current.style.top = response.top;
						dragSelectorRef.current.style.width = response.width;
						dragSelectorRef.current.style.height = response.height;
					}
				},
			});
		});

		document.addEventListener('pointerover', (e) => {
			ShiftKeyMouseOver({
				event: e,
				enabled: !isTiny && !isSmall && !isMedium,
				gridViewRef,
				lastMousePosRef,
				mouseDownRef,
				selectedItemsRef,
				shiftKeyDownRef,
			});
		});

		document.addEventListener('keyup', (e) => {
			ShiftKeyChange({
				event: e,
				gridViewRef,
				shiftKeyChangeCallback: (state) => {
					shiftKeyDownRef.current = state;
				},
				disableFolderSelectionCallback: (state) => setDisableFolderSelection(state),
			});
		});

		document.addEventListener('keydown', (e) => {
			ShiftKeyChange({
				event: e,
				gridViewRef,
				shiftKeyChangeCallback: (state) => {
					shiftKeyDownRef.current = state;
				},
				disableFolderSelectionCallback: (state) => setDisableFolderSelection(state),
			});
		});

		return () => {
			document.removeEventListener('pointerup', () => DragSelectShiftKeyMouseUp);
			document.removeEventListener('pointermove', () => DragSelectMousePos);
			document.removeEventListener('pointerover', () => ShiftKeyMouseOver);
			document.removeEventListener('keyup', () => ShiftKeyChange);
			document.removeEventListener('keydown', () => ShiftKeyChange);
		};
	}, []);

	return (
		<>
			<ContentSidebarLayout
				ref={mailBoxesRef}
				navigationComponent={
					<MailboxNavigation
						ref={mailBoxNavigationRef}
						title={searchActive ? t('mailboxes.search.title') : mailBoxInfo.folderName}
						createFolderSuccessCallback={(e: any) => MoveSelectedItems(e)}
						addonContent={(isTiny || isSmall || isMedium) && <MailboxActions />}
					/>
				}
				extraContentClass={gridView ? `item-content-files ${gridSize.className}` : `item-content-list`}
				onPointerDown={(e) =>
					DragSelectMouseDown({
						event: e,
						mailBoxesRef,
						enabled: !isTiny && !isSmall && !isMedium,
						mouseDownCallback: (response) => {
							if (dragPositionRef.current) {
								dragPositionRef.current = {
									...dragPositionRef.current,
									startX: response.startX,
									startY: response.startY,
								};
							}

							if (dragSelectorRef.current) {
								dragSelectorRef.current.style.opacity = '1';
							}

							mouseDownRef.current = true;

							setMouseDown(response.state);
							setDragWasActive(response.state);
						},
					})
				}
				extraClass='mailbox-items-rows'
			>
				{!searchActive && !isTiny && !isSmall && !isMedium && <MailboxHeader />}

				{searchActive && !isTiny && !isSmall && !isMedium && <MailboxSearchHeader />}

				{searchActive && searchParams.size > 0 && (
					<ItemRowToolBar sidePadding title='Mail Search Pills'>
						<ItemRowCell>
							<MailboxSearchPills />
						</ItemRowCell>
					</ItemRowToolBar>
				)}

				<MailboxListHeader
					data={data}
					status={status}
					mailBoxInfo={mailBoxInfo}
					gridView={gridView}
					selectedItems={selectedItems}
					allSelected={allSelected}
					visibleIndeterminate={visibleIndeterminate}
					visibleSelected={visibleSelected}
					toggleVisibleCallback={(toggleData: any, key: string, value: boolean) =>
						ToggleVisible(toggleData, key, value)
					}
					resetSelectionCallback={() => ResetSelection()}
				/>

				{!searchActive &&
					itemCounts.loaded !== itemCounts.total &&
					selectedItems.length !== itemCounts.total &&
					visibleSelected && (
						<SelectEverythingBanner
							selectedCount={allSelected ? itemCounts.total : selectedItems.length}
							selectedLabel={
								allSelected
									? t('actions.selectLoaded')
									: t('actions.selectAllIn', { location: mailBoxInfo.folderName })
							}
							callback={() => ToggleAll(!allSelected)}
						/>
					)}

				<MailboxEntries
					itemCounts={itemCounts}
					status={status}
					error={error}
					isFetchingNextPage={isFetchingNextPage}
					fetchNextPage={fetchNextPage}
					dataUpdatedAt={dataUpdatedAt}
					data={data}
					hasNextPage={hasNextPage}
					searchActive={searchActive}
					customFolderData={customFolderData}
					mailBoxInfo={mailBoxInfo}
					gridView={gridView}
					allSelected={allSelected}
					selectedItems={selectedItems}
					gridViewRef={gridViewRef}
					selectedItemsRef={selectedItemsRef}
					shiftKeyDownRef={shiftKeyDownRef}
					draggingActiveCallback={(state: boolean) => setDraggingActive(state)}
					handleFolderMoveCallback={(result: any) => HandleFolderMove(result)}
					toggleSelectedCallback={(key: any, totalItems: number, value?: boolean | undefined) =>
						ToggleSelected(key, totalItems)
					}
					moveSelectedItemsCallback={(targetFolderId: number) => MoveSelectedItems(targetFolderId)}
					visibleSelected={visibleSelected}
					toggleAllCallback={(value: boolean) => ToggleAll(value)}
					isStale={isStale}
					isRefetching={isRefetching}
				/>

				<div ref={dragSelectorRef} className='dragger-block' />
			</ContentSidebarLayout>

			{status === 'success' && (
				<DropdownMenu
					title='Selected Mailboxes'
					classActivators={['mailbox-item', 'mailbox-item-block', 'mailbox-item-meta', 'mail-actions-menu']}
					classBlockers={['mailbox-item-row-header', 'mail-item-forward-preview', 'custom-folder-list-entry']}
					contextActivated
					activationCallBack={(el: any) => {
						if (
							el.hasAttribute('data-item-id') &&
							!selectedItems.includes(el.getAttribute('data-item-id'))
						) {
							ToggleSelected(el.getAttribute('data-item-id'), itemCounts.total, true);
						}
					}}
				>
					<MailboxesSelectedMenu
						data={data}
						showCustomFolderRemove={
							mailBoxInfo.customFolderId !== null && mailBoxInfo.customFolderId !== -1
						}
					/>
				</DropdownMenu>
			)}

			<DragHelper selectedCount={selectedItems.length} />
		</>
	);
}

export { MailboxRoot };
